import type { Blocker } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface DiscardChangesDialogProps {
  open: boolean;
  /** Keep editing — dismiss the confirmation, stay where you are. */
  onStay: () => void;
  /** Leave and lose the unsaved changes. */
  onDiscard: () => void;
}

/**
 * Controlled "unsaved changes" confirmation — for close paths that don't go
 * through the router (Escape / overlay-click / X on a dirty drawer or dialog).
 * Router navigations use {@link UnsavedChangesDialog} with a Blocker instead.
 */
export function DiscardChangesDialog({
  open,
  onStay,
  onDiscard,
}: DiscardChangesDialogProps) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onStay()}>
      <DialogContent className="sm:max-w-[425px]" data-testid="discard-changes-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("تغييرات غير محفوظة", "Unsaved Changes")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "لديك تغييرات لم يتم حفظها. هل تريد المغادرة وفقدان التغييرات؟",
              "You have unsaved changes. Do you want to leave and lose your changes?"
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onStay} data-testid="discard-changes-stay">
            {t("البقاء", "Stay")}
          </Button>
          <Button variant="destructive" onClick={onDiscard} data-testid="discard-changes-leave">
            {t("مغادرة", "Leave")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface UnsavedChangesDialogProps {
  blocker: Blocker;
}

export function UnsavedChangesDialog({ blocker }: UnsavedChangesDialogProps) {
  // Keep the dialog mounted (open=false) instead of early-returning null so
  // Radix can play the close animation when the blocker resolves.
  return (
    <DiscardChangesDialog
      open={blocker.state === "blocked"}
      onStay={() => blocker.reset?.()}
      onDiscard={() => blocker.proceed?.()}
    />
  );
}
